import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ChevronDown, Paperclip, Sparkles, UserPlus, X } from 'lucide-react';
import { coupleShortName } from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import EmailMessage from '@/components/EmailMessage';
import MatchHealthPanel from '@/components/MatchHealthPanel';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatInstant } from '@/lib/date';

/**
 * The Match Review Queue (PRD §F5) — "when you're not sure, ask me."
 *
 * Nothing on this screen has touched Gmail yet. Confirming is the moment a
 * label gets applied, which is why each item leads with the system's guess
 * *and its reasoning*: the planner is being asked to ratify a decision, and
 * they can only do that if they can see what it was based on.
 */

type Item = {
  id: string;
  candidateMatches: unknown;
  messageId: string;
  gmailMessageId: string;
  fromAddress: string | null;
  subject: string | null;
  snippet: string | null;
  sentAt: string;
  hasAttachments: boolean;
  sameSenderCount: number;
};

type Candidate = {
  inquiryId: string;
  weddingId: string;
  coupleNames: string;
  vendorName: string;
  confidence: number;
  rationale: string;
};

export default function ReviewPage() {
  const queryClient = useQueryClient();
  const [creatingFor, setCreatingFor] = useState<Item | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['review'],
    queryFn: async () => {
      const res = await client.api.review.$get();
      if (!res.ok) throw new Error('Failed to load the review queue');
      return res.json();
    },
  });

  const { data: suppressed } = useQuery({
    queryKey: ['review-suppressed'],
    queryFn: async () => {
      const res = await client.api.review.suppressed.$get();
      if (!res.ok) throw new Error('Failed to load suppressed senders');
      return res.json();
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['review'] });
    queryClient.invalidateQueries({ queryKey: ['review-count'] });
    queryClient.invalidateQueries({ queryKey: ['review-suppressed'] });
    // The dashboard renders this queue too, and its count comes from its own
    // endpoint — without this, resolving here leaves a stale "Needs filing"
    // section behind on Today.
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['wedding'] });
  };

  const confirm = useMutation({
    mutationFn: async ({ id, inquiryId }: { id: string; inquiryId: string }) => {
      const res = await client.api.review[':id'].confirm.$post({
        param: { id },
        json: { inquiryId },
      });
      if (!res.ok) throw new Error(((await res.json()) as { error?: string }).error ?? 'Failed');
      return res.json();
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(r.labelled ? 'Filed and labelled in Gmail' : 'Filed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkConfirm = useMutation({
    mutationFn: async ({ ids, inquiryId }: { ids: string[]; inquiryId: string }) => {
      const res = await client.api.review['bulk-confirm'].$post({
        json: { reviewItemIds: ids, inquiryId },
      });
      if (!res.ok) throw new Error('Bulk confirm failed');
      return res.json();
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(`Filed ${r.resolved} messages`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dismiss = useMutation({
    mutationFn: async ({ id, suppressSender }: { id: string; suppressSender: boolean }) => {
      const res = await client.api.review[':id'].dismiss.$post({
        param: { id },
        json: { suppressSender },
      });
      if (!res.ok) throw new Error('Failed to dismiss');
      return res.json();
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(r.suppressed ? `Dismissed. Future mail from ${r.suppressed} won't be queued.` : 'Dismissed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unsuppress = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api.review.suppressed[':id'].$delete({ param: { id } });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast.success('Sender un-silenced');
    },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const items = (data?.items ?? []) as Item[];
  const openInquiries = data?.openInquiries ?? [];

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h1 className="display-tight text-3xl font-semibold">Review</h1>
        <p className="text-sm text-muted-foreground">
          Mail we weren't confident enough to file on our own. Nothing here has been labelled yet.
        </p>
      </div>

      {data?.autoDismissed ? (
        <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-sm text-muted-foreground">
          {data.autoDismissed} {data.autoDismissed === 1 ? 'item' : 'items'} older than 30 days were
          dismissed automatically.
        </p>
      ) : null}

      {items.length === 0 ? (
        // PRD §F5: the zero state is celebrated, not blank. An inbox that never
        // empties teaches learned helplessness.
        <Card className="rounded-3xl bg-card p-12 text-center shadow-ambient">
          <Sparkles className="mx-auto size-8 text-primary" />
          <p className="display-tight mt-4 text-lg font-semibold">Nothing to review</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Every vendor reply has been filed on its own. This is what caught up looks like.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <ReviewCard
              key={item.id}
              item={item}
              openInquiries={openInquiries}
              busy={confirm.isPending || dismiss.isPending || bulkConfirm.isPending}
              onConfirm={(inquiryId) => confirm.mutate({ id: item.id, inquiryId })}
              onBulkConfirm={(inquiryId) =>
                bulkConfirm.mutate({
                  ids: items.filter((i) => i.fromAddress === item.fromAddress).map((i) => i.id),
                  inquiryId,
                })
              }
              onDismiss={(suppressSender) => dismiss.mutate({ id: item.id, suppressSender })}
              onCreateVendor={() => setCreatingFor(item)}
            />
          ))}
        </div>
      )}

      {suppressed && suppressed.length > 0 && (
        <div className="space-y-2">
          <h2 className="label-caps text-xs text-muted-foreground">
            Silenced senders ({suppressed.length})
          </h2>
          <Card className="rounded-3xl bg-card p-4 shadow-ambient">
            {suppressed.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-1.5 text-sm">
                <span>{s.email}</span>
                <Button variant="ghost" size="sm" onClick={() => unsuppress.mutate(s.id)}>
                  Un-silence
                </Button>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* The health readout belongs to this queue, not to Settings: these two
          rates are the leading indicator of how much lands here. */}
      <MatchHealthPanel />

      <CreateVendorDialog
        item={creatingFor}
        onOpenChange={(open) => !open && setCreatingFor(null)}
        onDone={invalidate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReviewCard({
  item,
  openInquiries,
  busy,
  onConfirm,
  onBulkConfirm,
  onDismiss,
  onCreateVendor,
}: {
  item: Item;
  openInquiries: {
    inquiryId: string;
    coupleNames: string;
    vendorName: string;
    category: string;
  }[];
  busy: boolean;
  onConfirm: (inquiryId: string) => void;
  onBulkConfirm: (inquiryId: string) => void;
  onDismiss: (suppressSender: boolean) => void;
  onCreateVendor: () => void;
}) {
  const candidates = (item.candidateMatches ?? []) as Candidate[];
  // Tier 4 names an inquiry (a known vendor, unclear which wedding). Tier 5
  // only names a wedding, because the sender is not a vendor we know yet —
  // still worth showing, since it is what makes "new vendor from this sender"
  // a one-click action instead of a lookup.
  const ranked = candidates.filter((c) => c.inquiryId).slice(0, 3);
  const weddingGuesses = candidates.filter((c) => !c.inquiryId && c.weddingId).slice(0, 3);
  const [chosen, setChosen] = useState<string>(ranked[0]?.inquiryId ?? '');
  const [showAll, setShowAll] = useState(false);

  // Unlike the inquiry page, which loads one conversation whole, this screen is
  // a list of unrelated messages — fetching every body on mount is the cost the
  // on-demand endpoint exists to avoid. So the read stays a deliberate click.
  const [wantBody, setWantBody] = useState(false);
  const { data: fetched, isLoading: bodyLoading } = useQuery({
    queryKey: ['message-body', item.gmailMessageId],
    queryFn: async () => {
      const res = await client.api.gmail.messages[':gmailMessageId'].body.$get({
        param: { gmailMessageId: item.gmailMessageId },
      });
      if (!res.ok) throw new Error('Could not load this message');
      return res.json();
    },
    enabled: wantBody,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Card className="rounded-3xl bg-card p-5 shadow-ambient">
      {/* Collapsed, this header is the whole queue row: who, what, the gist,
          when. Expanded, <EmailMessage/> below states sender, date and the full
          body itself — and the snippet is just a truncated copy of that body —
          so those lines stand down rather than say it all twice. The subject is
          the one thing EmailMessage won't repeat (we pass it as the thread
          subject), so it stays and becomes the headline. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {!wantBody && (
            <p className="text-sm font-medium">
              {item.fromAddress}
              {item.hasAttachments && <Paperclip className="ml-2 inline size-3" />}
            </p>
          )}
          <p className={`truncate text-sm ${wantBody ? 'font-medium' : ''}`}>{item.subject}</p>
          {!wantBody && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.snippet}</p>
          )}
        </div>
        {!wantBody && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatInstant(item.sentAt, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>

      {!wantBody ? (
        <button
          className="mt-2 text-xs text-muted-foreground underline underline-offset-2"
          onClick={() => setWantBody(true)}
        >
          Read the full message
        </button>
      ) : bodyLoading ? (
        <p className="mt-2 text-xs text-muted-foreground">Fetching from Gmail…</p>
      ) : (
        <div className="mt-2 rounded-2xl bg-[var(--color-surface-container-high)] px-3">
          <EmailMessage
            message={{
              id: item.messageId,
              // Everything in this queue is an unmatched *reply* — the planner
              // never sends from here.
              direction: 'inbound',
              fromAddress: item.fromAddress,
              sentAt: item.sentAt,
              subject: item.subject,
              matchTier: null,
              snippet: item.snippet,
              attachments: fetched?.attachments ?? [],
              body: fetched?.body ?? null,
              bodyError: fetched ? null : 'unavailable',
            }}
            threadSubject={item.subject}
          />
        </div>
      )}

      {/* The system's reasoning, shown before the choice. PRD §6.2: attribute
          every automated action — "we think this because…" */}
      <div className="mt-4 space-y-2 border-t border-border pt-4">
        {ranked.length > 0 ? (
          <>
            <p className="label-caps text-xs text-muted-foreground">Best guess</p>
            {ranked.map((cand) => (
              <label
                key={cand.inquiryId}
                className="flex cursor-pointer items-start gap-3 rounded-2xl px-3 py-2 text-sm hover:bg-[var(--color-surface-container-high)]"
              >
                <input
                  type="radio"
                  name={`cand-${item.id}`}
                  className="mt-1"
                  checked={chosen === cand.inquiryId}
                  onChange={() => setChosen(cand.inquiryId)}
                />
                <span className="min-w-0">
                  <span className="block font-medium">
                    {cand.coupleNames} · {cand.vendorName}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {cand.rationale} · {Math.round(cand.confidence * 100)}% confident
                  </span>
                </span>
              </label>
            ))}
          </>
        ) : weddingGuesses.length > 0 ? (
          <>
            <p className="label-caps text-xs text-muted-foreground">Looks related to</p>
            {weddingGuesses.map((g) => (
              <div key={g.weddingId} className="px-3 py-1 text-sm">
                <span className="font-medium">{g.coupleNames}</span>
                <span className="block text-xs text-muted-foreground">
                  {g.rationale} · {Math.round(g.confidence * 100)}% confident
                </span>
              </div>
            ))}
            <p className="px-3 text-xs text-muted-foreground">
              We don't recognize this sender, so there's no inquiry to file against yet — add them
              as a vendor, or pick an existing inquiry below.
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            No confident guess — pick where this belongs.
          </p>
        )}

        <button
          className="flex items-center gap-1 px-3 text-xs text-muted-foreground underline underline-offset-2"
          onClick={() => setShowAll((v) => !v)}
        >
          <ChevronDown className={`size-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
          {showAll ? 'Hide' : 'File somewhere else'}
        </button>

        {showAll && (
          <div className="px-3">
            <Select value={chosen} onValueChange={setChosen}>
              <SelectTrigger>
                <SelectValue placeholder="Pick an inquiry" />
              </SelectTrigger>
              <SelectContent>
                {openInquiries.map((i) => (
                  <SelectItem key={i.inquiryId} value={i.inquiryId}>
                    {i.coupleNames} · {i.vendorName} ({i.category})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!chosen || busy} onClick={() => onConfirm(chosen)}>
            <Check className="size-3.5" />
            File it
          </Button>
          {item.sameSenderCount > 1 && (
            <Button
              variant="outline"
              size="sm"
              disabled={!chosen || busy}
              onClick={() => onBulkConfirm(chosen)}
            >
              File all {item.sameSenderCount} from this sender
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={busy} onClick={onCreateVendor}>
            <UserPlus className="size-3.5" />
            New vendor from this sender
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onDismiss(false)}>
            <X className="size-3.5" />
            Not relevant
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onDismiss(true)}>
            Never from this sender
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function CreateVendorDialog({
  item,
  onOpenChange,
  onDone,
}: {
  item: Item | null;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [businessName, setBusinessName] = useState('');
  const [category, setCategory] = useState('');

  // Default to the wedding the matcher pointed at. It is right often enough to
  // save a lookup, and wrong cheaply — the select is right there.
  const guessedWeddingId =
    ((item?.candidateMatches ?? []) as Candidate[]).find((c) => c.weddingId)?.weddingId ?? '';
  const [weddingIdOverride, setWeddingIdOverride] = useState<string | null>(null);
  const weddingId = weddingIdOverride ?? guessedWeddingId;
  const setWeddingId = (v: string) => setWeddingIdOverride(v);

  const { data: weddings } = useQuery({
    queryKey: ['weddings'],
    queryFn: async () => {
      const res = await client.api.weddings.$get();
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!item,
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!item,
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await client.api.review[':id']['create-vendor'].$post({
        param: { id: item!.id },
        json: { businessName: effectiveName, weddingId, category, primaryCategory: category },
      });
      if (!res.ok) throw new Error(((await res.json()) as { error?: string }).error ?? 'Failed');
      return res.json();
    },
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      setBusinessName('');
      toast.success('Vendor created and message filed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Seed the name from the sender's domain — usually close, always editable.
  const suggested = useMemo(() => {
    const addr = item?.fromAddress ?? '';
    const domain = addr.slice(addr.lastIndexOf('@') + 1).split('.')[0] ?? '';
    return domain ? domain.charAt(0).toUpperCase() + domain.slice(1) : '';
  }, [item]);

  const effectiveName = businessName.trim() || suggested;

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New vendor from {item?.fromAddress}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bn">Business name</Label>
            <Input
              id="bn"
              autoFocus
              placeholder={suggested}
              value={businessName || suggested}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Wedding</Label>
            <Select value={weddingId} onValueChange={setWeddingId}>
              <SelectTrigger>
                <SelectValue placeholder="Which wedding?" />
              </SelectTrigger>
              <SelectContent>
                {(weddings ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {/* This picker reads the weddings *list*, which serves whole
                        rows — unlike the review payloads above, which carry a
                        `coupleNames` the server derived for them. */}
                    {coupleShortName(w)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="What do they do?" />
              </SelectTrigger>
              <SelectContent>
                {(categories ?? []).map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Creates the vendor, opens an inquiry on that wedding at{' '}
            <strong>replied</strong> — they wrote to you, not the other way round — and files this
            message against it.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!effectiveName || !weddingId || !category || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create & file'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Nav badge — PRD §F5 wants the count visible globally. */
export function useReviewCount() {
  return useQuery({
    queryKey: ['review-count'],
    queryFn: async () => {
      const res = await client.api.review.count.$get();
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 60_000,
  });
}
